<template>
    <div
        ref="rootRef"
        class="am-select"
        :class="[
            `am-select--${variant}`,
            { 'am-select--open': isOpen, 'am-select--disabled': disabled },
        ]"
    >
        <button
            type="button"
            class="am-select__trigger"
            :disabled="disabled"
            :aria-expanded="isOpen"
            aria-haspopup="listbox"
            @click="toggle"
        >
            <ChatIcon v-if="icon" :name="icon" :size="15" class="am-select__icon" />
            <span class="am-select__value">{{ displayLabel }}</span>
            <ChatIcon name="arrow-down" :size="14" class="am-select__chevron" />
        </button>

        <div v-if="isOpen" class="am-select__dropdown" role="listbox" @click.stop>
            <input
                v-if="searchable"
                ref="searchRef"
                v-model="searchQuery"
                type="text"
                class="am-select__search"
                :placeholder="searchPlaceholder"
                @keydown.stop
            />
            <button
                v-for="opt in filteredOptions"
                :key="String(opt.value)"
                type="button"
                role="option"
                class="am-select__option"
                :class="{ 'am-select__option--selected': opt.value === modelValue }"
                :aria-selected="opt.value === modelValue"
                @click="select(opt.value)"
            >
                {{ opt.label }}
            </button>
            <p v-if="filteredOptions.length === 0" class="am-select__empty">Ничего не найдено</p>
        </div>
    </div>
</template>

<script setup>
import { computed, nextTick, onBeforeUnmount, onMounted, ref, watch } from 'vue'
import ChatIcon from '@/components/chat/icons/ChatIcon.vue'

const props = defineProps({
    modelValue: { type: [String, Number], default: '' },
    options: { type: Array, default: () => [] },
    placeholder: { type: String, default: 'Выберите…' },
    searchable: { type: Boolean, default: false },
    searchPlaceholder: { type: String, default: 'Поиск…' },
    disabled: { type: Boolean, default: false },
    icon: { type: String, default: '' },
    variant: {
        type: String,
        default: 'field',
        validator: v => ['field', 'filter'].includes(v),
    },
})

const emit = defineEmits(['update:modelValue'])

const rootRef = ref(null)
const searchRef = ref(null)
const isOpen = ref(false)
const searchQuery = ref('')

const filteredOptions = computed(() => {
    const q = searchQuery.value.trim().toLowerCase()
    if (!q) return props.options
    return props.options.filter(o => String(o.label).toLowerCase().includes(q))
})

const displayLabel = computed(() => {
    const found = props.options.find(o => o.value === props.modelValue)
    if (found) return found.label
    if (props.modelValue === '' || props.modelValue == null) return props.placeholder
    return props.placeholder
})

function toggle() {
    if (props.disabled) return
    isOpen.value = !isOpen.value
    if (isOpen.value && props.searchable) {
        searchQuery.value = ''
        nextTick(() => searchRef.value?.focus())
    }
}

function select(value) {
    emit('update:modelValue', value)
    isOpen.value = false
    searchQuery.value = ''
}

function onDocumentPointerDown(e) {
    if (!isOpen.value) return
    if (rootRef.value && !rootRef.value.contains(e.target)) {
        isOpen.value = false
    }
}

watch(isOpen, open => {
    if (!open) searchQuery.value = ''
})

onMounted(() => document.addEventListener('pointerdown', onDocumentPointerDown))
onBeforeUnmount(() => document.removeEventListener('pointerdown', onDocumentPointerDown))
</script>

<style scoped>
@import './ui-tokens.css';

.am-select {
    position: relative;
    width: 100%;
}

.am-select--filter {
    width: auto;
    display: inline-block;
}

.am-select__trigger {
    width: 100%;
    display: inline-flex;
    align-items: center;
    gap: 8px;
    border: 1px solid var(--ui-border);
    background: var(--ui-surface);
    color: var(--ui-text);
    font-family: inherit;
    font-size: 13px;
    cursor: pointer;
    text-align: left;
    transition: border-color 0.12s ease, background 0.12s ease;
}

.am-select--field .am-select__trigger {
    padding: 12px 14px;
    border-radius: var(--ui-radius);
    min-height: 44px;
    box-sizing: border-box;
}

.am-select--filter .am-select__trigger {
    padding: 6px 12px;
    border-radius: var(--ui-radius-pill);
    min-height: 36px;
    background: var(--ui-bg);
    font-size: 12.5px;
}

.am-select__trigger:hover:not(:disabled),
.am-select--open .am-select__trigger {
    border-color: var(--ui-dark);
}

.am-select--disabled .am-select__trigger {
    opacity: 0.6;
    cursor: not-allowed;
    background: var(--ui-bg);
}

.am-select__value {
    flex: 1;
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
}

.am-select__icon {
    flex-shrink: 0;
    color: var(--ui-muted);
}

.am-select__chevron {
    flex-shrink: 0;
    color: var(--ui-muted);
    transition: transform 0.15s ease;
}

.am-select--open .am-select__chevron {
    transform: rotate(180deg);
}

.am-select__dropdown {
    position: absolute;
    left: 0;
    right: 0;
    top: calc(100% + 6px);
    z-index: 120;
    max-height: 260px;
    overflow-y: auto;
    padding: 8px;
    border: 1px solid var(--ui-border);
    border-radius: var(--ui-radius);
    background: var(--ui-surface);
    box-shadow: var(--ui-shadow-dropdown);
}

.am-select--filter .am-select__dropdown {
    min-width: 200px;
}

.am-select__search {
    width: 100%;
    box-sizing: border-box;
    border: 1px solid var(--ui-border);
    border-radius: 8px;
    outline: 0;
    padding: 8px 10px;
    font-family: inherit;
    font-size: 12px;
    color: var(--ui-text);
    margin-bottom: 4px;
}

.am-select__search:focus {
    border-color: var(--ui-dark);
}

.am-select__option {
    width: 100%;
    display: block;
    margin-top: 2px;
    border: 0;
    border-radius: 8px;
    background: transparent;
    color: var(--ui-text);
    font-family: inherit;
    font-size: 13px;
    text-align: left;
    padding: 8px 10px;
    cursor: pointer;
}

.am-select__option:hover,
.am-select__option--selected {
    background: #F7F5EF;
}

.am-select__empty {
    margin: 8px 10px;
    font-size: 12px;
    color: var(--ui-muted);
}
</style>
